import { Check, FileDown, QrCode, ShieldCheck } from "lucide-react";
import { cn } from "@/lib/utils";
import { AppFrame, Panel, Row, StatCard } from "./app-frame";

/**
 * As três telas dos blocos principais da seção de recursos.
 *
 * Cada uma segue a mesma partitura — linha de indicadores, painel principal,
 * faixa de rodapé — porque a repetição é o que faz as três lerem como o mesmo
 * sistema. Ao mexer numa, confira se as outras continuam com o mesmo ritmo
 * vertical: elas aparecem lado a lado no scroll.
 *
 * No mobile cada tela mostra dois indicadores em vez de três, e o painel perde
 * a última linha (ver app-frame.tsx). O que sai é sempre o dado de apoio; os
 * dois que ficam são os que sustentam a promessa do bloco em features.tsx —
 * então o par não é escolha estética, é o argumento da seção. Se o título do
 * bloco mudar, reveja qual indicador fica.
 */

/* ── 01 · Pessoas ───────────────────────────────────────────────────── */

export function PessoasScreen() {
  return (
    <AppFrame
      active="Pessoas"
      breadcrumb={["Início", "Pessoas"]}
      title="Pessoas"
      subtitle="Central da Esperança · Piracicaba, SP"
    >
      {/* "Ninguém escapa pelo vão": o par que prova isso é o total de gente
          na base e quem o sistema está sinalizando. "Visitantes no mês" é
          apoio e sai no mobile. */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
        <StatCard
          label="Pessoas ativas"
          value="1.647"
          sub="de 1.812 no total"
        />
        <StatCard
          label="Visitantes no mês"
          value="38"
          sub="24 já retornaram"
          tone="positive"
          className="hidden md:flex"
        />
        <StatCard
          label="Precisam de contato"
          value="6"
          sub="atrasados"
          tone="alert"
        />
      </div>

      <Panel title="Precisam de atenção" action="Ver todos">
        <div className="flex flex-col gap-1.5">
          <Row
            avatar="MO"
            title="Marcos Oliveira"
            sub="Visitante · 3ª semana"
            chip="Ligar hoje"
            chipTone="alert"
          />
          <Row
            avatar="AL"
            title="Ana Lima"
            sub="Membro · faltou 3 domingos"
            chip="Sem contato"
            chipTone="alert"
          />
          <Row
            avatar="JP"
            title="João Pedro Santos"
            sub="Célula Betel · líder"
            chip="Em dia"
            chipTone="positive"
          />
          <Row
            avatar="LM"
            title="Laura Mendes"
            sub="Kids · Sala Arca, 4-6 anos"
            chip="Na sala"
            chipTone="positive"
            className="hidden md:flex"
          />
        </div>
      </Panel>

      <div className="flex items-center gap-1.5 px-2.5 py-2 rounded-lg bg-primary/5 border border-primary/15">
        <QrCode className="w-3.5 h-3.5 md:w-3 md:h-3 text-primary shrink-0" />
        <p className="text-[11px] md:text-[9px] text-muted-foreground leading-snug">
          Check-in do infantil por QR Code · retirada só por responsável
          autorizado
        </p>
      </div>
    </AppFrame>
  );
}

/* ── 02 · Escalas ───────────────────────────────────────────────────── */

const SHIFTS = [
  { day: "Dom", date: "1", role: "Louvor", people: "Gabriel, Ana +2" },
  { day: "Qua", date: "4", role: "Recepção", people: "Carlos, Beatriz" },
  { day: "Dom", date: "8", role: "Mídia", people: "Pedro, Rafa" },
  { day: "Qua", date: "11", role: "Louvor", people: "Gabriel, Luana +1" },
];

export function EscalasScreen() {
  return (
    <AppFrame
      active="Ministérios"
      breadcrumb={["Início", "Ministérios", "Escalas"]}
      title="Escalas · Junho"
      subtitle="Geradas automaticamente a partir das regras"
    >
      {/* "A escala do mês em 15 minutos": o par é o mês todo preenchido e
          zero conflito. A contagem de voluntários é apoio e sai no mobile. */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
        <StatCard label="Cultos no mês" value="9" sub="todos preenchidos" />
        <StatCard
          label="Voluntários"
          value="32"
          sub="dentro do limite"
          tone="positive"
          className="hidden md:flex"
        />
        <StatCard
          label="Conflitos"
          value="0"
          sub="nenhum a resolver"
          tone="positive"
        />
      </div>

      <Panel title="Escala do mês" action="Enviar a todos">
        <div className="flex flex-col gap-1.5">
          {SHIFTS.map((s, i) => (
            <div
              key={`${s.day}-${s.date}`}
              className={cn(
                "flex items-center gap-2 px-2 py-1.5 rounded-lg bg-secondary/25",
                // a 4ª data repete o padrão das anteriores, não traz dado novo
                i === 3 && "hidden md:flex",
              )}
            >
              <div className="w-8 md:w-7 shrink-0 text-center">
                <p className="text-[10px] md:text-[8px] text-muted-foreground leading-none">
                  {s.day}
                </p>
                <p className="text-[12px] md:text-[11px] font-bold text-foreground leading-tight">
                  {s.date}
                </p>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[12px] md:text-[10px] font-medium text-foreground truncate">
                  {s.role}
                </p>
                <p className="text-[11px] md:text-[9px] text-muted-foreground truncate">
                  {s.people}
                </p>
              </div>
              <Check className="w-3.5 h-3.5 md:w-3 md:h-3 text-status-good shrink-0" />
            </div>
          ))}
        </div>
      </Panel>

      <div className="flex items-center gap-1.5 px-2.5 py-2 rounded-lg bg-primary/5 border border-primary/15">
        <ShieldCheck className="w-3.5 h-3.5 md:w-3 md:h-3 text-primary shrink-0" />
        <p className="text-[11px] md:text-[9px] text-muted-foreground leading-snug">
          Respeitou indisponibilidade, culto preferido e limite por voluntário
        </p>
      </div>
    </AppFrame>
  );
}

/* ── 03 · Finanças ──────────────────────────────────────────────────── */

const SOURCES = [
  { label: "Dízimos", value: "R$ 12.480", pct: 61, bar: "bg-primary" },
  { label: "Ofertas", value: "R$ 4.920", pct: 24, bar: "bg-chart-7" },
  { label: "Cantina", value: "R$ 1.850", pct: 9, bar: "bg-chart-4" },
  { label: "Eventos", value: "R$ 1.200", pct: 6, bar: "bg-chart-6" },
];

export function FinancasScreen() {
  return (
    <AppFrame
      active="Finanças"
      breadcrumb={["Início", "Finanças"]}
      title="Finanças · Junho"
      subtitle="Mês fechado em 12 de julho · conferido"
    >
      {/* "Fechar o mês sem planilha": entrou e saldo é o que a diretoria
          pergunta. "Saiu" é apoio e sai no mobile — com dois cards em vez de
          três os valores em R$ param de estourar a largura. */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
        <StatCard
          label="Entrou"
          value="R$ 20.450"
          sub="14% a mais que maio"
          tone="positive"
        />
        <StatCard
          label="Saiu"
          value="R$ 14.180"
          sub="32 lançamentos"
          className="hidden md:flex"
        />
        <StatCard label="Saldo" value="R$ 6.270" sub="em 3 contas" />
      </div>

      <Panel title="De onde veio" action="Ver lançamentos">
        <div className="flex flex-col gap-2 pt-0.5">
          {SOURCES.map((s) => (
            <div key={s.label} className="flex items-center gap-2">
              <p className="text-[11px] md:text-[9px] text-muted-foreground w-16 md:w-12 shrink-0">
                {s.label}
              </p>
              <div className="flex-1 h-2 md:h-1.5 bg-secondary rounded-full overflow-hidden">
                <div
                  className={cn("h-full rounded-full", s.bar)}
                  style={{ width: `${s.pct}%` }}
                />
              </div>
              <p className="text-[11px] md:text-[9px] font-medium text-foreground w-20 md:w-16 text-right shrink-0 tabular-nums">
                {s.value}
              </p>
            </div>
          ))}
        </div>
      </Panel>

      <div className="flex items-center gap-1.5 px-2.5 py-2 rounded-lg bg-primary/5 border border-primary/15">
        <FileDown className="w-3.5 h-3.5 md:w-3 md:h-3 text-primary shrink-0" />
        <p className="text-[11px] md:text-[9px] text-muted-foreground leading-snug">
          Relatório da diretoria em PDF e exportação pronta para o contador
        </p>
      </div>
    </AppFrame>
  );
}
